import React, { useEffect, useState } from 'react';
import { BOTTOM_INDENTS, COLORS } from '@constants';
import { CUSTOMERS_STATUS, ORDERS_STATUS } from '@enums';
import { usePolling, useStore } from '@hooks';
import { Divider, Icon, Typography } from '@ui';
import { Checkbox, Flex } from 'antd';
import { observer } from 'mobx-react';
import styled from 'styled-components';
import LineItem from '../../../../../../../Profile/components/AccountSettings/components/Integrations/shared/LineItem';

const StyledWrapperNoShadow = styled.div`
    padding: 32px 24px 0px 24px;
    border: 1px solid ${COLORS.$gray15Black};
    border-radius: 8px;
    position: relative;
`;

const StyledHeader = styled(Flex)`
    gap: 24px;
    align-items: center;
`;

const StyledTitle = styled(Typography.Title)`
    font-size: 24px;
    line-height: 36px;
    font-weight: 500;
    margin: 0;
    color: #04080f;
`;

const StyledCheckboxWrapper = styled.div`
    margin-bottom: 24px;
`;

const StyledContent = styled(Flex)`
    flex-direction: column;
    gap: 24px;
`;

const AdditionalSetup = observer(() => {
    const store = useStore();
    const {
        setupInfo,
        getSetupInfo,
        historicalSetupOrdersPending,
        historicalSetupCustomersPending,
        currentAccount,
        updateAccount
    } = store.accountsStore;
    const [modal, setModal] = useState(null);
    const { startPolling, stopPolling } = usePolling(getSetupInfo, 10000, false, 2000);

    useEffect(() => {
        if (historicalSetupOrdersPending || historicalSetupCustomersPending) {
            startPolling();
        } else {
            stopPolling();
        }
    }, [historicalSetupOrdersPending, historicalSetupCustomersPending]);

    const settings = currentAccount?.settings || {};
    const [subDomain, setSubDomain] = useState(settings.hasThirdPartyDomain || false);

    useEffect(() => {
        const currentValue = settings.hasThirdPartyDomain || false;
        setSubDomain(currentValue);
    }, [settings]);

    const handleSubDomainChange = async (e) => {
        const newValue = e.target.checked;
        setSubDomain(newValue);

        try {
            await updateAccount(
                {
                    settings: {
                        hasThirdPartyDomain: newValue
                    }
                },
                'json'
            );
        } catch (error) {
            setSubDomain(!newValue);
        }
    };

    const setupConfig = {
        orders: {
            title: 'Orders setup',
            status: historicalSetupOrdersPending
                ? ORDERS_STATUS.PENDING
                : setupInfo.historical.hasOrders
                ? ORDERS_STATUS.CONNECTED
                : ORDERS_STATUS.NOT_CONNECTED
        },
        customers: {
            title: 'Customers setup',
            status: historicalSetupCustomersPending
                ? CUSTOMERS_STATUS.PENDING
                : setupInfo.historical.hasCustomers
                ? CUSTOMERS_STATUS.CONNECTED
                : CUSTOMERS_STATUS.NOT_CONNECTED
        }
    };

    return (
        <StyledWrapperNoShadow>
            <StyledContent>
                <StyledHeader>
                    <Icon.KlicklyLogo size='56px' style={{ flexShrink: 0, width: '56px', height: '56px' }} />
                    <StyledTitle level={2}>Additional setup</StyledTitle>
                </StyledHeader>
                <Divider style={{ margin: 0 }} />
                <StyledCheckboxWrapper>
                    <Checkbox checked={subDomain} onChange={handleSubDomainChange}>
                        <Typography.Text type='descriptor1'>
                            I have third-party landing pages or other pages hosted on a sub-domain or different domain
                        </Typography.Text>
                    </Checkbox>
                </StyledCheckboxWrapper>
            </StyledContent>
            {Object.entries(setupConfig).map(([key, data]) => (
                <LineItem
                    setModal={setModal}
                    modal={modal}
                    key={key}
                    setupKey={key}
                    data={data}
                    isManual
                    bottomMargin={BOTTOM_INDENTS.M}
                    showTooltip
                    tooltipText='To activate new and cold customer commissions, Klickly’s system needs to know when a shopper last bought from your store.'
                />
            ))}
        </StyledWrapperNoShadow>
    );
});

export default AdditionalSetup;
